import { Reveal } from "./Reveal";
import { BlurImage } from "@/components/BlurImage";

export default function About({ content, consultants = [] }) {
  const sections = content.about_sections?.length
    ? content.about_sections
    : (content.about_body ? [{ heading: "", body: content.about_body }] : []);
  const values = content.values || [];
  const team = consultants.length ? consultants : (content.principals || []);
  const principals = team.filter((p) => p.is_principal !== false);
  const employees = team.filter((p) => p.is_principal === false);
  const Member = (p, i, prefix) => (
    <Reveal key={p.id || p.name || i} delay={i * 0.1}>
      <div data-testid={`${prefix}-${i}`} className="group">
        <div className="overflow-hidden border border-upland-brown/20 aspect-[891/1047]">
          <BlurImage src={p.image_url} alt={p.name} placeholder={p.image_placeholder}
            className="w-full h-full object-cover object-center group-hover:scale-105" />
        </div>
        <h4 className="font-serif text-xl text-upland-green mt-4">{p.name}</h4>
        <p className="text-upland-brown text-xs tracking-wide uppercase mt-1">{p.role}</p>
        <p className="text-upland-grey mt-2 leading-relaxed text-sm">{p.bio}</p>
      </div>
    </Reveal>
  );

  return (
    <section id="about" data-testid="about-section" className="bg-upland-creme py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-upland-brown mb-5">{content.about_overline}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-upland-green">{content.about_title}</h2>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7 space-y-8">
            {sections.map((s, i) => (
              <Reveal key={s.heading || `section-${i}`} delay={0.1 + i * 0.1}>
                {s.heading && <h3 className="font-serif text-2xl text-upland-green mb-2">{s.heading}</h3>}
                <p className="text-upland-grey text-lg leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Values — P.A.C.E. */}
        {values.length > 0 && (
          <div className="mt-24" data-testid="values-block">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-upland-brown mb-4">{content.values_overline || "Our Values"}</p>
              <h3 className="font-serif text-3xl md:text-5xl text-upland-green max-w-2xl leading-tight">{content.values_title}</h3>
              {content.values_intro && <p className="text-upland-grey text-lg mt-5 max-w-2xl leading-relaxed">{content.values_intro}</p>}
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-upland-brown/20 border border-upland-brown/20">
              {values.map((v, i) => (
                <Reveal key={v.letter || v.name || `value-${i}`} delay={i * 0.08}>
                  <div data-testid={`value-${i}`} className="bg-upland-offwhite h-full p-8">
                    <div className="font-serif text-6xl text-upland-green/15 leading-none">{v.letter}</div>
                    <h4 className="font-serif text-2xl text-upland-green mt-3">{v.name}</h4>
                    <p className="text-upland-grey mt-3 leading-relaxed text-sm">{v.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        {content.about_stats?.length > 0 && (
          <Reveal delay={0.1}>
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-upland-brown/30">
              {content.about_stats.map((s, i) => (
                <div key={s.label || `stat-${i}`} data-testid={`stat-${i}`} className="border-b border-upland-brown/30 md:border-b-0 md:border-r last:border-r-0 border-upland-brown/30 py-10 md:py-12 px-2 md:px-6">
                  <div className="font-serif text-4xl md:text-5xl text-upland-green">{s.value}</div>
                  <div className="text-sm text-upland-grey mt-2 tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {/* Principals */}
        {principals.length > 0 && (
          <div className="mt-24">
            <Reveal>
              <h3 className="font-serif text-3xl md:text-4xl text-upland-green mb-12">{content.principals_heading || "The principals"}</h3>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {principals.map((p, i) => Member(p, i, "principal"))}
            </div>
          </div>
        )}

        {/* Employees */}
        {employees.length > 0 && (
          <div className="mt-20" data-testid="employees-section">
            <Reveal>
              <h3 className="font-serif text-3xl md:text-4xl text-upland-green mb-12">{content.employees_heading || "Our team"}</h3>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {employees.map((p, i) => Member(p, i, "employee"))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
